import {
  Button,
  Icon,
  LoadingSpinner,
  Typography,
} from '@data-products-and-ai/react-components';
import { Box, Divider, Stack } from '@mui/material';
import { useEffect, useState, useRef } from 'react';
import useOAuth from '@/hooks/useOAuth';
import { useToast } from '@/contexts/Toast/ToastContext';
import useUploadFile from '@/hooks/useUploadFile';
import { useAccessToken } from '@/hooks';
import { ApiConnector } from '@/services/ApiConnector';

/**
 * UploadPPRZone Component
 * Manages the state and operations for uploading and ingesting a PPR file into Snowflake
 * Uses various hooks for OAuth, file upload, job status checking, and workflow launching.
 */
const UploadPPRZone = () => {
  const url = import.meta.env.VITE_ALTERYX_API_URL;
  const token = useOAuth(
    url,
    import.meta.env.VITE_ALTERYX_CLIENT_ID,
    import.meta.env.VITE_ALTERYX_CLIENT_SECRET,
  ).accessToken;

  const { getSilentToken } = useAccessToken();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isStartWorkflowEnabled, setIsStartWorkflowEnabled] = useState(false);
  const [status, setStatus] = useState('idle');
  const statusRef = useRef(status);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const { showToast } = useToast();
  const { uploadFile } = useUploadFile(getSilentToken);

  const checkPipelineStatus = async (
    interval: NodeJS.Timeout,
    success: () => void,
  ) => {
    try {
      // Get fresh token before API call to avoid 401 errors from expired tokens
      const accessToken = await getSilentToken();
      
      const urlInputFileResponse = await ApiConnector(
        'GET',
        `ingestion`,
        {
          baseUrl: import.meta.env.VITE_API_URL,
          accessToken: accessToken,
        },
        { 'Content-Type': 'application/json', Accept: 'application/json' },
      );
      if (urlInputFileResponse.status === 200) {
        const currentStatus = urlInputFileResponse.data['pipeline'];
        if (currentStatus === 'idle') {
          clearInterval(interval);
          if (statusRef.current === 'running') {
            if (urlInputFileResponse.data['failureReason']) {
              showToast(
                `File ingestion failed: ${urlInputFileResponse.data['failureReason']}`,
                'error',
              );
              setIsStartWorkflowEnabled(true);
              setStatus('idle');
              return;
            }
          }
          success();
        }
        if (currentStatus === 'running') {
          setStatus('running');
        }
        if (currentStatus === 'failed') {
          showToast('The latest upload failed. Please try again.', 'error');
          clearInterval(interval);
          setStatus('idle');
          setIsStartWorkflowEnabled(true);
        }
      }
    } catch (error) {
      console.error('Failed to fetch pipeline status', error);
    }
  };

  const checkInitialPipelineStatus = async (interval: NodeJS.Timeout) => {
    try {
      // Get fresh token before API call to avoid 401 errors from expired tokens
      const accessToken = await getSilentToken();
      
      const urlInputFileResponse = await ApiConnector(
        'GET',
        `ingestion`,
        {
          baseUrl: import.meta.env.VITE_API_URL,
          accessToken: accessToken,
        },
        { 'Content-Type': 'application/json', Accept: 'application/json' },
      );
      if (urlInputFileResponse.status === 200) {
        const currentStatus = urlInputFileResponse.data['pipeline'];
        if (currentStatus === 'idle') {
          setIsStartWorkflowEnabled(true);
          setStatus('idle');
          showToast('Ready to upload!', 'info');
          clearInterval(interval);
        }
        if (currentStatus === 'running') {
          showToast('Another ingestion is in progress. Please wait...', 'info');
          setStatus('running');
        }
      }
    } catch (error) {
      console.error('Failed to fetch initial pipeline status', error);
    }
  };

  const checkStartPipelineStatus = async (
    interval: NodeJS.Timeout,
    success: () => void,
  ) => {
    try {
      // Get fresh token before API call to avoid 401 errors from expired tokens
      const accessToken = await getSilentToken();
      
      const urlInputFileResponse = await ApiConnector(
        'GET',
        `ingestion`,
        {
          baseUrl: import.meta.env.VITE_API_URL,
          accessToken: accessToken,
        },
        { 'Content-Type': 'application/json', Accept: 'application/json' },
      );
      if (urlInputFileResponse.status === 200) {
        const currentStatus = urlInputFileResponse.data['pipeline'];
        if (currentStatus !== 'idle') {
          clearInterval(interval);
          setStatus('running');
          success();
        }
      }
    } catch (error) {
      console.error('Failed to fetch start pipeline status', error);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const interval = setInterval(async () => {
        checkInitialPipelineStatus(interval);
      }, 5000);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Please select a file to upload.');
      return;
    }
    setIsStartWorkflowEnabled(false);
    try {
      showToast(
        `Uploading file ${selectedFile.name}. Please wait for the completion message.`,
        'info',
      );
      uploadFile(selectedFile)
        .then(() => {
          showToast(
            `File ${selectedFile.name} uploaded successfully! Preparing to start the processing workflow.`,
            'info',
          );
          const interval = setInterval(async () => {
            checkStartPipelineStatus(interval, () => {
              setStatus('running');
              showToast(
                'Processing in Database...',
                'info',
              );
              const uploadInterval = setInterval(async () => {
                  checkPipelineStatus(uploadInterval, () => {
                    showToast(
                      'File ingestion completed successfully!',
                      'success',
                    );
                    setSelectedFile(null);
                    setStatus('idle');
                    setIsStartWorkflowEnabled(true);
                  });
              }, 180000); // Check every 3 minutes
            });
          }, 5000);
        })
        .catch(() => {
          showToast(`File upload failed. Please try again.`, 'error');
          setIsStartWorkflowEnabled(true);
          setStatus('idle');
        });
    } catch (error) {
      showToast('Something went wrong. Please try the flow again.', 'error');
      setStatus('idle');
      setIsStartWorkflowEnabled(true);
    }
  };

  return (
    <Stack
      gap={2}
      sx={{
        justifyContent: 'space-between',
        alignItems: 'stretch',
        padding: '20px',
      }}
    >
      <div>
        <Typography tag="textsmall">Step 1. Choose the PPR file:</Typography>
        <Box
          onClick={() => {
            document.getElementById('library-images')?.click();
          }}
          style={{
            cursor: 'pointer',
            padding: '20px',
            border: '1px dashed #ccc',
            borderRadius: '8px',
            backgroundColor: '#f9f9f9',
            transition: 'background-color 0.3s',
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = '#e6e6e6')
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = '#f9f9f9')
          }
        >
          <Stack
            direction={'row'}
            gap={2}
            sx={{
              justifyContent: 'center',
              alignItems: 'stretch',
            }}
          >
            <Icon icon={'IconFolder'} />
            <Typography tag="textdefault">
              {selectedFile ? selectedFile.name : 'Choose file'}
            </Typography>
          </Stack>
        </Box>
        <input
          type="file"
          name="library-images"
          id="library-images"
          accept=".xls,.xlsx"
          hidden
          onChange={handleFileChange}
        />
      </div>
      <Typography tag="textsmall">
        Step 2. Upload and start the validation workflow:
      </Typography>
      <Button
        type="primary"
        width="100%"
        onClick={handleUpload}
        disabled={!isStartWorkflowEnabled || status !== 'idle'}
      >
        {!isStartWorkflowEnabled && selectedFile ? (
          <LoadingSpinner size={20} />
        ) : (
          'Start Workflow'
        )}
      </Button>
    </Stack>
  );
};

export default UploadPPRZone;
